/**
 * 日志中心 - 登录 / 操作 / 抓取 4 类
 */

import { useState, useEffect } from 'react';
import {
  Activity, User, Database, AlertCircle, Filter, RefreshCw, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const TABS = [
  { id: 'login', name: '登录日志', icon: User, color: 'text-brand-cyan' },
  { id: 'fetch', name: '抓取日志', icon: Database, color: 'text-brand-gold' },
];

export function LogsView() {
  const [tab, setTab] = useState('login');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');  // all / success / failed

  async function load() {
    setLoading(true);
    try {
      let res;
      if (tab === 'login') {
        res = await api.admin.logs({ pageSize: 200 });
        setLogs(Array.isArray(res?.logs) ? res.logs : []);
      } else if (tab === 'fetch') {
        res = await api.admin.fetchLogs({ pageSize: 200 });
        setLogs(Array.isArray(res?.logs) ? res.logs : []);
      } else if (tab === 'error') {
        res = await api.admin.errors({ pageSize: 200 });
        setLogs(Array.isArray(res?.errors) ? res.errors : []);
      } else {
        setLogs([]);
      }
    } catch (e) {
      console.error('LogsView load error:', e);
      toast.error('加载失败:' + e.message);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [tab]);

  const filtered = filter === 'all'
    ? logs
    : tab === 'login'
      ? logs.filter(l => filter === 'success' ? l.success : !l.success)
      : logs.filter(l => filter === 'success' ? l.status === 'ok' : l.status === 'failed');

  return (
    <div className="space-y-2">
      {/* Tab 切换 */}
      <div className="card !p-1.5 flex items-center gap-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setFilter('all'); }}
            className={clsx(
              'flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] transition',
              tab === t.id
                ? 'bg-white/8 text-white'
                : 'text-ink-3 hover:bg-white/4'
            )}
          >
            <t.icon size={12} className={t.color} />
            {t.name}
          </button>
        ))}
        <div className="flex-1" />
        {tab === 'login' && (
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-bg-base border border-white/5 rounded-md px-1.5 py-0.5 text-[10px]">
            <option value="all">全部</option>
            <option value="success">成功</option>
            <option value="failed">失败</option>
          </select>
        )}
        <button onClick={load} className="btn-ghost text-[10px] flex items-center gap-1 py-0.5 px-1.5">
          <RefreshCw size={10} /> 刷新
        </button>
      </div>

      {/* 日志列表 */}
      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          {tab === 'login' ? <LoginLogsTable logs={filtered} /> : <FetchLogsTable logs={filtered} />}
        </div>
        {loading && <div className="text-center py-6 text-xs text-ink-3">加载中...</div>}
        {!loading && filtered.length === 0 && (
          <div className="py-12 text-center text-ink-3">
            <FileText size={32} className="mx-auto opacity-30 mb-2" />
            <div className="text-sm">暂无日志</div>
            <div className="text-[10px] mt-1 opacity-70">用户登录或抓取触发后会自动出现</div>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginLogsTable({ logs }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-white/2 text-[10px] text-ink-3">
        <tr>
          <th className="text-left py-1.5 px-2.5 font-medium">时间</th>
          <th className="text-left py-1.5 px-2.5 font-medium">用户</th>
          <th className="text-left py-1.5 px-2.5 font-medium">结果</th>
          <th className="text-left py-1.5 px-2.5 font-medium">IP</th>
          <th className="text-left py-1.5 px-2.5 font-medium">UA</th>
        </tr>
      </thead>
      <tbody>
        {logs.map(l => (
          <tr key={l.id} className="border-t border-white/3 hover:bg-white/3">
            <td className="py-1 px-2.5 text-[11px] text-ink-3 font-mono whitespace-nowrap">
              {new Date(l.at).toLocaleString('zh-CN', { hour12: false })}
            </td>
            <td className="py-1 px-2.5">
              <div className="text-[11px]">{l.name || '-'}</div>
              <div className="text-[9px] text-ink-3 font-mono">{l.user_phone || l.phone}</div>
            </td>
            <td className="py-1 px-2.5">
              {l.success
                ? <span className="text-[11px] text-brand-cyan">✓ 成功</span>
                : <span className="text-[11px] text-accent-danger">✗ {l.fail_reason}</span>
              }
            </td>
            <td className="py-1 px-2.5 text-[11px] font-mono text-ink-2">{l.ip}</td>
            <td className="py-1 px-2.5 text-[11px] text-ink-3 max-w-xs truncate" title={l.ua}>
              {l.ua}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FetchLogsTable({ logs }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-white/2 text-[10px] text-ink-3">
        <tr>
          <th className="text-left py-1.5 px-2.5 font-medium">开始时间</th>
          <th className="text-left py-1.5 px-2.5 font-medium">状态</th>
          <th className="text-left py-1.5 px-2.5 font-medium">来源</th>
          <th className="text-right py-1.5 px-2.5 font-medium">抓取</th>
          <th className="text-right py-1.5 px-2.5 font-medium">新增</th>
        </tr>
      </thead>
      <tbody>
        {logs.map(l => (
          <tr key={l.id} className="border-t border-white/3 hover:bg-white/3">
            <td className="py-1 px-2.5 text-[11px] text-ink-3 font-mono">
              {new Date(l.started_at).toLocaleString('zh-CN', { hour12: false })}
            </td>
            <td className="py-1 px-2.5">
              <span className={clsx(
                'text-[9px] px-1.5 py-0.5 rounded',
                l.status === 'ok' ? 'bg-brand-cyan/20 text-brand-cyan' :
                l.status === 'failed' ? 'bg-accent-danger/20 text-accent-danger' :
                'bg-white/10 text-ink-3'
              )}>
                {l.status}
              </span>
            </td>
            <td className="py-1 px-2.5 text-[11px]">{l.source}</td>
            <td className="py-1 px-2.5 text-right font-mono">{l.periods_fetched || 0}</td>
            <td className="py-1 px-2.5 text-right font-mono">
              {l.new_periods > 0 ? <span className="text-brand-cyan">+{l.new_periods}</span> : 0}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
